import { ageFromDob, daysToDueDate } from '@pamosi/shared';
// Patient-side printables — Phase 5-light (2026-10-05).
//
// Two one-shot printouts triggered from the reception registration
// surface:
//   1. openPatientCardWindow — the paper ID card the receptionist hands
//      a new patient (hospital number, name, phone, DOB, blood group).
//   2. openRegistrationSummaryWindow — a human-readable dump of the
//      Section A–F form so the admitting clinician has a legible paper
//      copy in the chart.
//
// Both reuse the pattern from EncounterChargesSection.openReceiptWindow:
// pop a new window, write a self-contained HTML document, auto-call
// window.print() after a short tick. No Puppeteer, no backend — the
// pilot clinic prints to PDF via the browser.

import type {
    PatientCard,
    PatientHmoCoverage,
    PatientRegistrationDetails,
    RegSectionKey,
} from '@pamosi/shared';
import { PATIENT_CARD_TYPE_LABELS } from '@pamosi/shared';

// ─── Patient ID card ─────────────────────────────────────────────────────

export interface PatientCardPrintArgs {
    clinicName: string;
    patient: {
        full_name: string | null;
        phone_e164: string;
    };
    card: PatientCard;
    registration: PatientRegistrationDetails | null;
}

export function openPatientCardWindow(args: PatientCardPrintArgs): void {
    // Browser pop-up blocker tolerance: this is a direct user click, so
    // most browsers permit it. If the call fails the clinician can retry.
    const win = window.open('', '_blank', 'width=480,height=620');
    if (!win) return;

    const nameLine = args.patient.full_name?.trim() || '(No name on file)';
    const hospitalNumber = args.card.hospital_number ?? '—';
    const cardTypeLabel = PATIENT_CARD_TYPE_LABELS[args.card.card_type];
    const openedAt = new Date(args.card.opened_at).toLocaleDateString();
    const dob = args.registration?.dob
        ? new Date(args.registration.dob).toLocaleDateString()
        : '—';
    const genotype = formatEnum(args.registration?.genotype);
    const bloodGroup = formatBloodGroup(
        args.registration?.blood_group_abo,
        args.registration?.blood_group_rh,
    );
    const allergies = args.registration?.allergies_detail?.trim() || null;
    const printedOn = new Date().toLocaleString();

    win.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Patient card — ${escapeHtml(nameLine)}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #0f172a; margin: 24px; }
    .card { border: 1px solid #cbd5e1; border-radius: 10px; padding: 20px; max-width: 400px; }
    .clinic { font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #475569; }
    .card-type { font-size: 10px; color: #64748b; margin-top: 2px; }
    .hospital-number { font-size: 28px; font-weight: 700; letter-spacing: 0.03em; margin-top: 10px; font-variant-numeric: tabular-nums; }
    .name { font-size: 18px; font-weight: 600; margin-top: 14px; }
    .meta { font-size: 12px; color: #334155; margin-top: 2px; }
    .divider { border-top: 1px dashed #e2e8f0; margin: 14px 0; }
    .row { display: flex; gap: 12px; font-size: 12px; margin-top: 4px; }
    .row .k { color: #64748b; min-width: 90px; }
    .row .v { color: #0f172a; font-weight: 500; }
    .alert { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; font-size: 12px; padding: 8px 10px; border-radius: 6px; margin-top: 10px; }
    .footer { margin-top: 14px; font-size: 10px; color: #94a3b8; }
    @media print {
      body { margin: 10mm; }
      button { display: none; }
    }
  </style>
</head>
<body>
  <div class="card">
    <div class="clinic">${escapeHtml(args.clinicName)}</div>
    <div class="card-type">${escapeHtml(cardTypeLabel)}</div>
    <div class="hospital-number">#${escapeHtml(hospitalNumber)}</div>
    <div class="name">${escapeHtml(nameLine)}</div>
    <div class="meta">${escapeHtml(args.patient.phone_e164)}</div>
    <div class="divider"></div>
    <div class="row"><div class="k">Date of birth</div><div class="v">${escapeHtml(dob)}</div></div>
    <div class="row"><div class="k">Blood group</div><div class="v">${escapeHtml(bloodGroup)}</div></div>
    <div class="row"><div class="k">Genotype</div><div class="v">${escapeHtml(genotype)}</div></div>
    <div class="row"><div class="k">Card opened</div><div class="v">${escapeHtml(openedAt)}</div></div>
    ${allergies ? `<div class="alert"><strong>Allergies:</strong> ${escapeHtml(allergies)}</div>` : ''}
    <div class="footer">Printed ${escapeHtml(printedOn)}. Keep this card for every visit.</div>
  </div>
  <script>setTimeout(() => window.print(), 150);</script>
</body>
</html>`);
    win.document.close();
}

// ─── Registration summary ────────────────────────────────────────────────

export interface RegistrationSummaryPrintArgs {
    clinicName: string;
    patient: {
        full_name: string | null;
        phone_e164: string;
        identity_tier: string;
    };
    cards: PatientCard[];
    registration: PatientRegistrationDetails | null;
    hmo_coverages: PatientHmoCoverage[];
    hmoProviderName: (providerId: string) => string;
}

export function openRegistrationSummaryWindow(args: RegistrationSummaryPrintArgs): void {
    const win = window.open('', '_blank', 'width=760,height=900');
    if (!win) return;

    const name = args.patient.full_name?.trim() || '(No name on file)';
    const r = args.registration;
    const printedOn = new Date().toLocaleString();
    const completedSet = new Set<RegSectionKey>(r?.completed_sections ?? []);

    const activeCards = args.cards.filter((c) => c.status === 'active');
    const cardsHtml = activeCards.length === 0
        ? '<div class="empty">No active cards.</div>'
        : `<ul class="cards">${activeCards.map((c) => `
            <li>
                <span class="card-type">${escapeHtml(PATIENT_CARD_TYPE_LABELS[c.card_type])}</span>
                ${c.hospital_number ? `<span class="card-no">#${escapeHtml(c.hospital_number)}</span>` : ''}
                <span class="card-opened">opened ${escapeHtml(new Date(c.opened_at).toLocaleDateString())}</span>
            </li>
        `).join('')}</ul>`;

    const hmoHtml = args.hmo_coverages.length === 0
        ? '<div class="empty">No HMO coverage on file.</div>'
        : `<ul class="hmo">${args.hmo_coverages.map((h) => `
            <li>
                <div class="hmo-row">
                    <span class="hmo-provider">${escapeHtml(args.hmoProviderName(h.hmo_provider_id))}</span>
                    <span class="hmo-verif">${escapeHtml(h.verification_status)}</span>
                </div>
                <div class="hmo-meta">
                    Member #${escapeHtml(h.member_reference ?? '—')}
                    ${h.policy_number ? ` · policy ${escapeHtml(h.policy_number)}` : ''}
                    ${h.plan_name ? ` · ${escapeHtml(h.plan_name)}` : ''}
                </div>
            </li>
        `).join('')}</ul>`;

    const sectionsHtml = r
        ? renderRegistrationSections(r, completedSet)
        : '<div class="empty">No registration details recorded yet.</div>';

    win.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Registration summary — ${escapeHtml(name)}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #0f172a; margin: 24px; max-width: 720px; }
    h1 { font-size: 20px; margin: 0 0 2px; }
    h2 { font-size: 13px; margin: 20px 0 6px; text-transform: uppercase; letter-spacing: 0.05em; color: #475569; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; }
    .meta { color: #64748b; font-size: 12px; margin-bottom: 4px; }
    .section { margin-top: 10px; }
    .section .section-head { display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 600; color: #334155; }
    .section .section-head .status { font-size: 10px; padding: 1px 6px; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.05em; }
    .section .section-head .status.done { background: #d1fae5; color: #065f46; }
    .section .section-head .status.pending { background: #fef3c7; color: #92400e; }
    .fields { margin-top: 6px; display: grid; grid-template-columns: 1fr 1fr; gap: 4px 16px; font-size: 12px; }
    .fields .k { color: #64748b; }
    .fields .v { color: #0f172a; font-weight: 500; }
    .full { grid-column: 1 / -1; }
    ul.cards, ul.hmo { list-style: none; padding: 0; margin: 0; font-size: 12px; }
    ul.cards li, ul.hmo li { padding: 6px 0; border-bottom: 1px dashed #e2e8f0; }
    ul.cards .card-type { font-weight: 600; }
    ul.cards .card-no { margin-left: 8px; color: #475569; font-variant-numeric: tabular-nums; }
    ul.cards .card-opened { margin-left: 8px; color: #94a3b8; }
    .hmo-row { display: flex; justify-content: space-between; }
    .hmo-provider { font-weight: 600; }
    .hmo-verif { font-size: 10px; padding: 1px 6px; border-radius: 999px; background: #e0e7ff; color: #3730a3; text-transform: uppercase; letter-spacing: 0.05em; }
    .hmo-meta { color: #64748b; font-size: 11px; margin-top: 2px; }
    .empty { color: #94a3b8; font-size: 12px; font-style: italic; }
    .footer { margin-top: 32px; color: #94a3b8; font-size: 11px; }
    @media print {
      body { margin: 12mm; }
      button { display: none; }
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(args.clinicName)}</h1>
  <div class="meta">Patient registration summary</div>
  <div class="meta">
    Patient: <strong>${escapeHtml(name)}</strong> · ${escapeHtml(args.patient.phone_e164)}
    ${args.patient.identity_tier ? ` · ${escapeHtml(args.patient.identity_tier)}` : ''}
  </div>
  <div class="meta">Issued: ${escapeHtml(printedOn)}</div>

  <h2>Cards</h2>
  ${cardsHtml}

  <h2>HMO coverage</h2>
  ${hmoHtml}

  <h2>Registration</h2>
  ${sectionsHtml}

  <div class="footer">Printed from Pamosi Clinic. Hand-check against the paper form before filing.</div>
  <script>setTimeout(() => window.print(), 150);</script>
</body>
</html>`);
    win.document.close();
}

function renderRegistrationSections(
    r: PatientRegistrationDetails,
    completed: Set<RegSectionKey>,
): string {
    const sections: Array<{
        key: RegSectionKey;
        title: string;
        rows: Array<{ k: string; v: string | null; full?: boolean }>;
    }> = [
        {
            key: 'personal',
            title: 'A — Personal',
            rows: [
                { k: 'Date of birth',     v: formatDate(r.dob) },
                { k: r.dob ? 'Age from DOB' : 'Estimated age', v: r.dob ? String(ageFromDob(r.dob) ?? '—') + ' years' : r.estimated_age_years != null ? `${r.estimated_age_years} years` : null },
                { k: 'Age as of',         v: formatDate(r.estimated_age_as_of) },
                { k: 'Gender',            v: formatEnum(r.gender) },
                { k: 'Clinical sex',      v: r.clinical_sex },
                { k: 'Marital status',    v: formatEnum(r.marital_status) },
            ],
        },
        {
            key: 'contact',
            title: 'B — Contact',
            rows: [
                { k: 'Address',             v: r.address, full: true },
                { k: 'Primary phone',       v: r.phone_e164_primary },
                { k: 'Alternate phone',     v: r.phone_e164_alt },
                { k: 'Email',               v: r.email },
                { k: 'Preferred channel',   v: formatEnum(r.preferred_contact_channel) },
            ],
        },
        {
            key: 'socio',
            title: 'C — Socio-demographic',
            rows: [
                { k: 'Occupation',       v: r.occupation },
                { k: 'State of origin',  v: r.state_of_origin },
                { k: 'Ethnicity',        v: r.ethnicity },
                { k: 'Religion',         v: r.religion },
            ],
        },
        {
            key: 'social',
            title: 'D — Social history',
            rows: [
                { k: 'Smoking',              v: formatEnum(r.smoking) },
                { k: 'Alcohol',              v: formatEnum(r.alcohol) },
                { k: 'Recreational drugs',   v: formatYesNoUnknown(r.recreational_drugs) },
                { k: 'Drugs detail',         v: r.recreational_drugs_detail, full: true },
                { k: 'Living situation',     v: formatEnum(r.living_situation) },
                { k: 'Living detail',        v: r.living_situation_detail, full: true },
            ],
        },
        {
            key: 'medical',
            title: 'E — Medical history',
            rows: [
                { k: 'Genotype',             v: formatEnum(r.genotype) },
                { k: 'Blood group',          v: formatBloodGroup(r.blood_group_abo, r.blood_group_rh) },
                { k: 'Allergy status',       v: formatEnum(r.allergy_status) },
                { k: 'Allergies',            v: r.allergies_detail, full: true },
                { k: 'Chronic conditions',   v: r.chronic_conditions_detail, full: true },
                { k: 'Current medicines',    v: r.current_medicines_detail, full: true },
                { k: 'Surgical history',     v: r.surgical_history_detail, full: true },
            ],
        },
        {
            key: 'nok',
            title: 'F — Next of kin',
            rows: [
                { k: 'NOK name',             v: r.nok_name },
                { k: 'NOK phone',            v: r.nok_phone_e164 },
                { k: 'NOK relationship',     v: r.nok_relationship },
                { k: 'NOK address',          v: r.nok_address, full: true },
                { k: 'Alt. contact name',    v: r.alt_emergency_contact_name },
                { k: 'Alt. contact phone',   v: r.alt_emergency_contact_phone },
            ],
        },
    ];

    if (r.anc_details) {
        const a = r.anc_details;
        const num = (v: number | null) => v === null ? null : String(v);
        sections.push({key:'medical',title:'ANC — Pregnancy registration',rows:[
            {k:'LMP',v:a.lmp},{k:'LMP certain',v:formatYesNoUnknown(a.lmp_certain)},
            {k:'EDD',v:a.edd},{k:'EDD source',v:a.edd_source.replaceAll('_',' ')},
            {k:'Days to due date (at print time)',v:a.edd ? num(daysToDueDate(a.edd)) : null},
            {k:'Booking date',v:a.booking_date},{k:'Cycle length (days)',v:num(a.menstrual_cycle_days)},
            {k:'Flow duration (days)',v:num(a.menstrual_duration_days)},{k:'Menstrual flow',v:a.menstrual_flow},
            {k:'Gravidity',v:num(a.gravidity)},{k:'Parity',v:num(a.parity)},{k:'Living children',v:num(a.living_children)},
            {k:'Pregnancy losses',v:num(a.pregnancy_losses)},{k:'Fetuses',v:num(a.fetuses)},
            {k:'Dating notes',v:a.dating_notes,full:true},
            ...a.previous_deliveries.map((d,i)=>({k:`Previous delivery ${i+1}`,v:`${d.year ?? '—'} · ${d.mode.replaceAll('_',' ')} · ${d.outcome} · ${d.complications}`,full:true})),
            {k:'Maternal risk factors',v:a.risk_notes,full:true},{k:'ANC care plan',v:a.care_plan,full:true},
        ]});
    }

    return sections.map((s) => {
        const isDone = completed.has(s.key);
        const rowsHtml = s.rows.map((row) => `
            <div class="k${row.full ? ' full' : ''}">${escapeHtml(row.k)}</div>
            <div class="v${row.full ? ' full' : ''}">${escapeHtml(row.v && row.v.trim() !== '' ? row.v : '—')}</div>
        `).join('');
        return `
            <div class="section">
                <div class="section-head">
                    <span>${escapeHtml(s.title)}</span>
                    <span class="status ${isDone ? 'done' : 'pending'}">${isDone ? 'Complete' : 'Pending'}</span>
                </div>
                <div class="fields">${rowsHtml}</div>
            </div>
        `;
    }).join('');
}

// ─── Formatting helpers ──────────────────────────────────────────────────

function escapeHtml(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function formatDate(iso: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString();
}

function formatEnum(v: string | null | undefined): string {
    if (!v) return '—';
    return v.replace(/_/g, ' ');
}

function formatYesNoUnknown(v: boolean | null): string | null {
    if (v === null) return null;
    return v ? 'Yes' : 'No';
}

function formatBloodGroup(
    abo: string | null | undefined,
    rh: string | null | undefined,
): string {
    if (!abo || abo === 'unknown') return '—';
    const letters = abo.toUpperCase();
    const sign = rh === 'positive' ? '+' : rh === 'negative' ? '−' : '';
    return `${letters}${sign}`;
}
